import { useState } from "react";
import { useLocation } from "wouter";
import { useUser } from "contexts/UserContext";

// ✅ AÑADIDO: Helper de reintento ante ERR_CONNECTION_RESET / Failed to fetch
async function fetchWithRetry(url, options, retries = 3, delayMs = 1000) {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      return await fetch(url, options);
    } catch (err) {
      if (attempt === retries) throw err;
      console.warn(`⚠️ Reintento ${attempt}/${retries} para ${url} en ${delayMs}ms...`);
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }
}

// Hook personalizado `useFetch` para realizar peticiones API y manejar su estado
export default function useFetch() {
  // Wouter - useLocation() permite cambiar de pantalla
  const [, setLocation] = useLocation();
  const { setUser } = useUser() || {};
  // Estados que gestionan el proceso de la petición:
  const [loading, setLoading] = useState(false);
  const [isCalled, setIsCalled] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  // Función `fetchAction` que realiza la petición API
  async function fetchAction(params) {
    // Reinicia los estados antes de realizar la nueva solicitud
    setIsCalled(true);
    setData(null);
    setError(null);
    setMessage(null);
    setLoading(true);
    // Auth: JWT si existe en localStorage (login, recuperar contraseña, etc. van sin cabecera)
    const storedUser = localStorage.getItem('user')
    const jwtToken = storedUser ? (() => { try { return JSON.parse(storedUser)?.token } catch { return null } })() : null
    try {
      let responseData = null;
      const isFormData = params.body instanceof FormData;
      const headers = isFormData ? {} : { "Content-Type": "application/json" };
      if (jwtToken) headers.Authorization = `Bearer ${jwtToken}`;
      // ✅ CAMBIADO: fetch() → fetchWithRetry() para reintentar ante fallos de red
      const response = await fetchWithRetry(
        `${process.env.REACT_APP_API_URL}/${params.url}`,
        {
          method: params.method,
          headers,
          body:
            params.method === "GET"
              ? undefined
              : isFormData
              ? params.body
              : JSON.stringify(params.body), // Evita JSON.stringify en FormData
        }
      );
      // Si la respuesta es exitosa (status 200-299)
      if (response.ok) {
        responseData = await response.json(); // CHANGED
        setData(responseData);
        setMessage(responseData?.message);
        return responseData; // CHANGED: devolver JSON de éxito
      }
      // Si la respuesta no es exitosa
      if (!response.ok) {
        // Si el error es 401 (token caducado o inválido) y no es una solicitud de autenticación,
        // se cierra la sesión: solo redirigir no basta, porque /login devuelve a "/" mientras
        // quede un token guardado y la app seguiría funcionando con todas las peticiones fallando.
        if (!params.url.includes("auth") && response.status === 401) {
          if (setUser) setUser({});
          setLocation("/login");
          return { error: "Session expired", status: 401 };
        } else {
          // Si es otro error, guarda el error de la respuesta
          const errorText = await response.text();
          console.error(`❌ Error ${response.status} en ${params.url}:`, errorText);
          setError(errorText);
          return { error: errorText, status: response.status }; // CHANGED: devolver error legible
        }
      }
    } catch (error) {
      console.error("❌ Error en useFetch:", error);
      setError(error); // Si ocurre un error durante la solicitud, se almacena el error
      return { error }; // CHANGED: devolver error lanzado
    } finally {
      setLoading(false); // Independientemente del resultado, se indica que la solicitud ha terminado
    }
  }
  // Retorna un array con el estado actual de la solicitud y la función `fetchAction`
  return [
    {
      loading, // Estado de carga
      isCalled, // Indica si la función fue llamada
      data, // Datos de la respuesta
      error, // Errores de la respuesta
      message, // Mensajes de la respuesta
    },
    fetchAction, // Función para realizar la solicitud
  ];
}